import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title: string;
  password?: string;
  isZoomUrl?: boolean;
  userEmail?: string;
  personId?: string;
  authToken?: string;
}

const VideoPreviewModal = ({ isOpen, onClose, videoUrl, title, password, isZoomUrl, userEmail, personId, authToken }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [directAuthUrl, setDirectAuthUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (!userEmail && !personId) return null;

      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const params = new URLSearchParams();
        if (userEmail) params.append('email', userEmail);
        if (personId) params.append('personId', personId);

        const response = await fetch(`${apiUrl}/api/zoom/get-access-token?${params.toString()}`, {
          headers: {
            'Authorization': authToken ? `Bearer ${authToken}` : '',
          },
        });
        if (response.ok) {
          const data = await response.json();
          return data.access_token;
        }
      } catch (error) {
        console.error('Failed to fetch access token:', error);
      }
      return null;
    };

    if (isOpen) {
      // Reset states when opening
      setError(null);
      setIsLoading(true);
      setDirectAuthUrl(null);

      // Check if this is a Zoom URL that needs authentication
      if ((isZoomUrl || videoUrl.includes('zoom.us')) && (userEmail || personId)) {
        // Use direct authentication with access_token query parameter
        fetchAccessToken().then(token => {
          if (token) {
            // Construct URL with access token (similar to create-story-from-recording)
            let authUrl = `${videoUrl}?access_token=${token}`;
            if (password) {
              // Add password if available for password-protected recordings
              authUrl += `&playback_access_token=${password}`;
            }
            setDirectAuthUrl(authUrl);
            setIsLoading(false);
          } else {
            console.error('Could not get access token for Zoom recording');
            setError('Unable to authenticate with Zoom. Please open in Zoom Web Player.');
            setIsLoading(false);
          }

          if (videoRef.current) {
            videoRef.current.load();
          }
        });
      } else {
        setDirectAuthUrl(null);
        setIsLoading(false);

        if (videoRef.current) {
          videoRef.current.load();
        }
      }
    }
  }, [isOpen, videoUrl, isZoomUrl, userEmail, personId, password]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  };

  const handleError = () => {
    if ((isZoomUrl || videoUrl.includes('zoom.us'))) {
      setError('Unable to play Zoom recording. Click below to open in Zoom Web Player.');
    } else {
      setError('Failed to load video.');
    }
    setIsLoading(false);
  };

  const handleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handlePlaybackRateChange = (rate: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSkip = (seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Title>{title}</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <VideoContainer>
          {isLoading && <LoadingMessage>Loading video...</LoadingMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {(isZoomUrl || videoUrl.includes('zoom.us')) && !directAuthUrl ? (
            // For Zoom URLs, show a message with link to open in new window
            <ZoomMessage>
              <ZoomIcon>🎥</ZoomIcon>
              <ZoomText>
                Zoom recordings cannot be embedded directly due to authentication requirements.
              </ZoomText>
              <ZoomActions>
                <OpenZoomButton
                  onClick={() => window.open(videoUrl, '_blank')}
                  rel="noopener noreferrer"
                >
                  Open in Zoom Web Player →
                </OpenZoomButton>
                {password && (
                  <PasswordInfo>
                    Password: <code>{password}</code>
                  </PasswordInfo>
                )}
              </ZoomActions>
              <ZoomNote>
                The recording will open in a new window where you can sign in to view it.
              </ZoomNote>
            </ZoomMessage>
          ) : (
            <Video
              ref={videoRef}
              controls={false}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onError={handleError}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            >
              <source
                src={directAuthUrl || videoUrl}
                type="video/mp4"
                onError={() => console.log('Video source error. Current URL:', directAuthUrl || videoUrl)}
              />
              Your browser does not support the video tag.
            </Video>
          )}
        </VideoContainer>

        {!error && (!(isZoomUrl || videoUrl.includes('zoom.us')) || directAuthUrl) && (
          <Controls>
            <ControlRow>
              <PlayButton onClick={handlePlayPause}>
                {isPlaying ? '⏸' : '▶'}
              </PlayButton>

              <TimeDisplay>
                {formatTime(currentTime)} / {formatTime(duration)}
              </TimeDisplay>

              <SeekBar
                type="range"
                min="0"
                max={duration}
                value={currentTime}
                onChange={handleSeek}
              />

              <MuteButton onClick={handleMute}>
                {isMuted ? '🔇' : '🔊'}
              </MuteButton>
            </ControlRow>

            <ControlRow>
              <SkipButton onClick={() => handleSkip(-10)}>
                ⏪ 10s
              </SkipButton>
              <SkipButton onClick={() => handleSkip(10)}>
                10s ⏩
              </SkipButton>

              <SpeedControl>
                Speed:
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                  <SpeedButton
                    key={rate}
                    active={playbackRate === rate}
                    onClick={() => handlePlaybackRateChange(rate)}
                  >
                    {rate}x
                  </SpeedButton>
                ))}
              </SpeedControl>
            </ControlRow>

            {password && (
              <PasswordNote>
                🔐 Password: <PasswordText>{password}</PasswordText>
              </PasswordNote>
            )}
          </Controls>
        )}

        <InfoText>
          Note: This is a preview. For full Zoom features, use the share URL.
        </InfoText>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #484848;
`;

const Title = styled.h3`
  margin: 0;
  color: #f3e9d7;
  font-size: 16px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #808080;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #f3e9d7;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  background: #000;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const LoadingMessage = styled.div`
  position: absolute;
  color: #a0a0a0;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  position: absolute;
  color: #ff4444;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const Controls = styled.div`
  padding: 16px 20px;
  background: #0a0a1a;
  border-top: 1px solid #484848;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PlayButton = styled.button`
  background: #45d483;
  border: none;
  color: #03041a;
  font-size: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #3bc372;
  }
`;

const TimeDisplay = styled.div`
  color: #a0a0a0;
  font-size: 12px;
  min-width: 80px;
`;

const SeekBar = styled.input`
  flex: 1;
  height: 6px;
  background: #484848;
  border-radius: 3px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    background: #45d483;
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: #45d483;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const MuteButton = styled.button`
  background: transparent;
  border: 1px solid #484848;
  color: #a0a0a0;
  font-size: 16px;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(69, 212, 131, 0.1);
    border-color: #45d483;
    color: #45d483;
  }
`;

const SkipButton = styled.button`
  background: transparent;
  border: 1px solid #484848;
  color: #a0a0a0;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: rgba(69, 212, 131, 0.1);
    border-color: #45d483;
    color: #45d483;
  }
`;

const SpeedControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  color: #a0a0a0;
  font-size: 12px;
`;

const SpeedButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(69, 212, 131, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.active ? '#45d483' : '#484848'};
  color: ${props => props.active ? '#45d483' : '#a0a0a0'};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: rgba(69, 212, 131, 0.1);
    border-color: #45d483;
  }
`;

const PasswordNote = styled.div`
  padding: 8px 12px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  color: #ffc107;
  font-size: 12px;
  margin-top: 8px;
`;

const PasswordText = styled.span`
  font-family: monospace;
  background: rgba(0, 0, 0, 0.3);
  padding: 2px 6px;
  border-radius: 3px;
  margin-left: 4px;
`;

const InfoText = styled.div`
  padding: 12px 20px;
  background: rgba(69, 212, 131, 0.05);
  border-top: 1px solid rgba(69, 212, 131, 0.2);
  color: #808080;
  font-size: 11px;
  text-align: center;
`;

const ZoomMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  gap: 20px;
`;

const ZoomIcon = styled.div`
  font-size: 48px;
`;

const ZoomText = styled.div`
  color: #f3e9d7;
  font-size: 16px;
  max-width: 400px;
`;

const ZoomActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const OpenZoomButton = styled.button`
  padding: 12px 24px;
  background: #45d483;
  color: #03041a;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #3bc372;
    transform: translateY(-1px);
  }
`;

const PasswordInfo = styled.div`
  padding: 8px 16px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  color: #ffc107;
  font-size: 13px;

  code {
    font-family: monospace;
    background: rgba(0, 0, 0, 0.3);
    padding: 2px 6px;
    border-radius: 3px;
    margin-left: 4px;
  }
`;

const ZoomNote = styled.div`
  color: #808080;
  font-size: 12px;
  max-width: 350px;
`;

export default VideoPreviewModal;